import React from 'react';
import { AlertCircle, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Inline alert block (NOT a toast) — the bordered, tinted message box that was copy-pasted as inline
 * Tailwind for errors/notices across ~7 files (Landing, modals, forms). Semantic colour is the one
 * sanctioned exception to monochrome. For a slim icon+action nudge use NoticeBar; for transient
 * pop-ups use Toast.
 */
export type AlertVariant = 'error' | 'warning' | 'info' | 'success';

const STYLE: Record<AlertVariant, { wrap: string; text: string; Icon: React.FC<{ className?: string }> }> = {
  error: { wrap: 'bg-error-50 border-error-200 dark:bg-error-900/20 dark:border-error-800', text: 'text-error-700 dark:text-error-400', Icon: AlertCircle },
  warning: { wrap: 'bg-warning-50 border-warning-200 dark:bg-warning-900/20 dark:border-warning-800', text: 'text-warning-700 dark:text-warning-400', Icon: AlertTriangle },
  info: { wrap: 'bg-secondary-50 border-secondary-200 dark:bg-secondary-900/60 dark:border-secondary-800', text: 'text-secondary-700 dark:text-secondary-300', Icon: Info },
  success: { wrap: 'bg-success-50 border-success-200 dark:bg-success-900/20 dark:border-success-800', text: 'text-success-700 dark:text-success-400', Icon: CheckCircle2 },
};

export interface AlertProps {
  variant?: AlertVariant;
  /** Optional bold heading above the message. */
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({ variant = 'error', title, children, className }) => {
  const s = STYLE[variant];
  return (
    <div className={cn('flex gap-2.5 rounded-lg border p-3', s.wrap, className)} role="alert">
      <s.Icon className={cn('w-4 h-4 mt-0.5 shrink-0', s.text)} />
      <div className={cn('text-sm min-w-0', s.text)}>
        {title && <p className="font-semibold">{title}</p>}
        <div className={title ? 'mt-0.5' : undefined}>{children}</div>
      </div>
    </div>
  );
};

export default Alert;
